import { useState, useCallback } from 'react';
import { Outlet, useLoaderData } from 'react-router';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import irWorkstationTheme, {
	irWorkstationDarkTheme,
} from 'features/IRWorkstation/styles/IRWorkstationTheme';
import { useThemeMode } from 'contexts/ThemeModeContext';
import Navbar from 'features/IRWorkstation/components/Layout/Navbar';
import Sidebar from 'features/IRWorkstation/components/Layout/Sidebar';
import NewsFeedDrawer from 'features/IRWorkstation/components/NewsFeed/NewsFeedDrawer';
import LPPanel from 'features/IRWorkstation/components/Layout/LPPanel';
import { IRWorkstationProvider } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { UserProvider } from 'contexts/UserContext';

const IRWorkstationLayout = () => {
	const { user, featureFlags } = useLoaderData();

	// Sets document title to "Investor Engagement Manager" for browser tab
	useDocumentTitle();

	const { mode } = useThemeMode();
	const currentTheme =
		mode === 'dark' ? irWorkstationDarkTheme : irWorkstationTheme;

	const [lpPanelOpen, setLpPanelOpen] = useState(false);
	const [selectedLp, setSelectedLp] = useState(null);

	// news feed drawer state and handlers
	const [newsFeedDrawerOpen, setNewsFeedDrawerOpen] = useState(false);
	const [newsFeed, setNewsFeed] = useState({});

	const handleOpenNewsFeedDrawer = useCallback((newsFeed) => {
		setNewsFeed(newsFeed);
		setNewsFeedDrawerOpen(true);
	}, []);

	const handleCloseNewsFeedDrawer = useCallback(() => {
		setNewsFeedDrawerOpen(false);
	}, []);

	const handleOpenLP = useCallback((lp) => {
		setSelectedLp(lp);
		setLpPanelOpen(true);
	}, []);

	const handleCloseLP = useCallback(() => {
		setLpPanelOpen(false);
	}, []);

	return (
		<UserProvider user={user}>
			<IRWorkstationProvider
				onOpenNewsFeedDrawer={handleOpenNewsFeedDrawer}
				featureFlags={featureFlags}
			>
				<ThemeProvider theme={currentTheme}>
					<CssBaseline />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							height: '100vh',
							width: '100vw',
							backgroundColor: 'background.default',
						}}
					>
						<Navbar onOpenLP={handleOpenLP} />

						<Box
							sx={{
								flex: 1,
								display: 'flex',
								minHeight: 0,
								position: 'relative',
								overflow: 'hidden',
							}}
						>
							<Sidebar />
							<Outlet />

							<NewsFeedDrawer
								newsFeed={newsFeed}
								open={newsFeedDrawerOpen}
								onClose={handleCloseNewsFeedDrawer}
							/>

							<LPPanel
								open={lpPanelOpen}
								lp={selectedLp}
								onClose={handleCloseLP}
							/>
						</Box>
					</Box>
				</ThemeProvider>
			</IRWorkstationProvider>
		</UserProvider>
	);
};

export default IRWorkstationLayout;
